import { makeStyles, Theme, createStyles, Typography } from "@material-ui/core";
import * as React from "react";
import { renderToString } from "react-dom/server";
import SimpleTable from "../../../components/SimpleTable";
import { EVENT_EXPORT_EXCEL_LEVEL_HISTORY, EVENT_EXPORT_PDF_LEVEL_HISTORY } from "../../../Events";
import { FETCH_EXCEL_FILE_LEVEL_HISTORY, FETCH_PDF_FILE_FROM_HTML } from "../../../Fedx";
import { formatDateTime, convertDateFromTimezonePtBr } from "../../../helpers/datetime";
import { calculateLevelPercentage, getLevelColor } from "../../../helpers/installationTankHelpers";
import { formatValue, isArrayValid } from "../../../helpers/utility";
import { useHubx, useHubXSubscribe } from "../../../HubX";
import { IExportExcel } from "../../../core/contracts/IExportExcel";
import IHtml from "../../../core/contracts/IHtml";
import ReportGrid from "../../../components/Report/ReportGrid";
import CustomerModel from "../../../core/models/Customer";
import { useSnackbar } from "notistack";
import ReportSimpleTable from "../../../components/Report/ReportSimpleTable";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxHeight: "30vh",
    },

    installationHeader: {
      padding: "16px",
      background: theme.palette.background.paper,
      color: theme.customPalette.text.boldgrey,
      fontWeight: "bold",
    },
  })
);

type ReportDataType = {
  date: string;
  level: number;
  level_in: string | number;
  status: string;
  tanqueId: string | undefined;
  capacity: string | number;
  product: string | undefined;
  installation: string | undefined;
};

const headers = [
  "Produto",
  "ID Instalação",
  "ID do Tanque",
  "Nível Instantâneo",
  "Capacidade",
  "% Nível",
  "Status de Nível",
  "Última Modificação",
];

type ReportLevelsProps = {
  filteredCustomers: CustomerModel[];
  filteredInstallationsIds: string[];
  statusFilter: string[];
};

function ReportLevels({
  filteredCustomers,
  filteredInstallationsIds,
  statusFilter,
}: ReportLevelsProps) {
  const hub = useHubx();
  const toastr = useSnackbar();

  const buildRowsFromCustomer = React.useCallback(
    (customer: CustomerModel) => {
      return (
        customer?.installations
          ?.filter((installation) => {
            if (isArrayValid(filteredInstallationsIds)) {
              return filteredInstallationsIds.includes(installation.installation_id ?? "");
            }
            return false;
          })
          ?.flatMap((installation) => {
            return (
              installation?.tanks
                ?.filter((x) => statusFilter.includes(x.tank_level_status ?? ""))
                ?.map((tank) => {
                  return [
                    tank.product_code,
                    installation.installation_id,
                    tank.tank_id,
                    formatValue(tank.tank_level_su),
                    formatValue(tank.tank_capacity_su),
                    formatValue(
                      calculateLevelPercentage(tank.tank_level_su, tank.tank_capacity_su),
                      1
                    ),
                    <span
                      key={tank.tank_id}
                      style={{ color: getLevelColor(tank.tank_level_status) }}
                    >
                      {tank.tank_level_status}
                    </span>,
                    <strong key={tank.tank_id}>
                      {convertDateFromTimezonePtBr(tank.last_rtu_reading_date)}
                    </strong>,
                  ];
                }) ?? []
            );
          }) ?? []
      );
    },
    [filteredInstallationsIds, statusFilter]
  );

  const memoDataToDisplay = React.useCallback(() => {
    if (isArrayValid(filteredCustomers)) {
      return filteredCustomers.map((customer) => {
        const rows = buildRowsFromCustomer(customer);

        return (
          <div key={customer.customer_id}>
            <div>
              <Typography className={classes.installationHeader} component="h5" variant="h5">
                {customer.customer_name}
              </Typography>
            </div>
            <SimpleTable header={headers} rows={rows} />
          </div>
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildRowsFromCustomer, filteredCustomers]);

  const getFileName = `_${formatDateTime(new Date(), "dd/MM/yyyy")}`;

  async function fetchFile() {
    const reportData: ReportDataType[] =
      filteredCustomers?.flatMap((customer) => {
        return (
          customer?.installations?.flatMap((installation) => {
            return (
              installation.tanks?.map((tank) => {
                return {
                  date: convertDateFromTimezonePtBr(tank.last_rtu_reading_date),
                  level: calculateLevelPercentage(tank.tank_level_su, tank.tank_capacity_su),
                  level_in: tank.tank_level_su,
                  status: tank.tank_level_status,
                  tanqueId: tank.tank_id,
                  capacity: tank.tank_capacity_su,
                  product: tank.product_code,
                  installation: installation.installation_id,
                } as ReportDataType;
              }) ?? []
            );
          }) ?? []
        );
      }) ?? [];

    const exportFile: IExportExcel = {
      headers: headers,
      data: reportData,
      fileName: `Relatório_Nivel${getFileName}`,
    };

    try {
      await hub.request(FETCH_EXCEL_FILE_LEVEL_HISTORY, exportFile);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("ReportLevels → fetchFile -> error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar baixar o relatório de níveis em Excel!",
        { variant: "error" }
      );
    }
  }

  async function fetchPdf() {
    const customHeight = filteredCustomers.length > 1 ? "85%" : "60%";
    const htmlFile: IHtml = {
      htmlString: renderToString(
        <ReportGrid headerReport="Relatório de Níveis">
          {filteredCustomers.map((customer) => {
            const rows = buildRowsFromCustomer(customer);
            return (
              <div key={`${customer.customer_id}`}>
                <div>
                  <div
                    style={{
                      margin: 0,
                      padding: 8,
                      background: "#E7E7E7",
                      color: "#1D212A",
                      fontWeight: "bold",
                      display: "flex",
                      columnGap: "16px",
                    }}
                  >
                    <div>
                      <div>{customer.customer_name}</div>
                    </div>
                  </div>
                </div>
                <div style={{ pageBreakAfter: "avoid" }}>
                  <ReportSimpleTable header={headers} rows={rows} customHeight={customHeight} />
                </div>
              </div>
            );
          })}
        </ReportGrid>
      ),
      fileName: `Relatório_Nivel${getFileName}`,
    };

    try {
      await hub.request(FETCH_PDF_FILE_FROM_HTML, htmlFile);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("ReportLevels → fetchFile -> error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar baixar o relatório de níveis em PDF!",
        { variant: "error" }
      );
    }
  }

  useHubXSubscribe(EVENT_EXPORT_EXCEL_LEVEL_HISTORY, fetchFile);
  useHubXSubscribe(EVENT_EXPORT_PDF_LEVEL_HISTORY, fetchPdf);

  const classes = useStyles();

  return (
    <div className={classes.root}>
      {memoDataToDisplay() || <SimpleTable header={headers} rows={[]} />}
    </div>
  );
}

export default ReportLevels;
